import { c as _c } from "react/compiler-runtime";
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import type { ProductTypeWithEmbroideryInformation } from '@/app/[abTest]/[locale]/(spreadshirt)/cyo/assortment/detail/actions/productType';
import { SizeButton } from '@/components/Sizes/SizeButton';
import { SizeFitHint } from '@/components/Sizes/SizeFitHint';
import { SizeGuideDialog } from '@/components/Sizes/SizeGuideDialog';
type SizeSelectorProps = {
  productType: ClientProductType;
  selectedAppearanceStockState: ProductTypeWithEmbroideryInformation['stockStates'] | ProductType['stockStates'];
  selectedSizeId: string | undefined;
  onSizeSelection: (sizeId: string) => void;
  platform: Platform;
  error?: boolean;
};
export const SizeFitHintAndGuide = t0 => {
  const $ = _c(11);
  const {
    className,
    productType,
    platform,
    isSellableGroup: t1
  } = t0;
  const isSellableGroup = t1 === undefined ? false : t1;
  const isOneSize = productType.sizes.length <= 1;
  if (isOneSize || isSellableGroup) {
    return null;
  }
  let t2;
  if ($[0] !== className) {
    t2 = clsx("flex items-center justify-between", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== productType.sizeFitHintId) {
    t3 = <SizeFitHint sizeFitHintId={productType.sizeFitHintId} className="text-sm" />;
    $[2] = productType.sizeFitHintId;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== platform || $[5] !== productType) {
    t4 = <SizeGuideDialog productType={productType} platform={platform} />;
    $[4] = platform;
    $[5] = productType;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = <div className={t2}>{t3}{t4}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};
export const SizeSelectorDesktop = t0 => {
  const $ = _c(26);
  const {
    productType,
    selectedSizeId,
    selectedAppearanceStockState,
    onSizeSelection,
    platform,
    error
  } = t0;
  const t = useTranslations();
  let t1;
  if ($[0] !== platform || $[1] !== productType) {
    t1 = <SizeFitHintAndGuide productType={productType} platform={platform} />;
    $[0] = platform;
    $[1] = productType;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = error && "animate-shake";
  let t3;
  if ($[3] !== t2) {
    t3 = clsx("flex flex-wrap gap-2", t2);
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("cyoAssortmentDetailPage.selectSize");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== error || $[8] !== onSizeSelection || $[9] !== productType.sizes || $[10] !== selectedAppearanceStockState || $[11] !== selectedSizeId) {
    let t6;
    if ($[13] !== error || $[14] !== onSizeSelection || $[15] !== productType.sizes.length || $[16] !== selectedAppearanceStockState || $[17] !== selectedSizeId) {
      t6 = size => {
        const inStock = selectedAppearanceStockState.some(stockState => stockState.size.id === size.id && stockState.available);
        if (productType.sizes.length === 1 && !inStock) {
          return <SizeButton key={size.id} selected={size.id === selectedSizeId} disabled={!inStock} error={error}>{size.name}</SizeButton>;
        }
        return <SizeButton key={size.id} onClick={() => onSizeSelection(size.id)} selected={size.id === selectedSizeId} disabled={!inStock} error={error}>{size.name}</SizeButton>;
      };
      $[13] = error;
      $[14] = onSizeSelection;
      $[15] = productType.sizes.length;
      $[16] = selectedAppearanceStockState;
      $[17] = selectedSizeId;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    t5 = productType.sizes.map(t6);
    $[7] = error;
    $[8] = onSizeSelection;
    $[9] = productType.sizes;
    $[10] = selectedAppearanceStockState;
    $[11] = selectedSizeId;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[19] !== t3 || $[20] !== t4 || $[21] !== t5) {
    t6 = <div role="listbox" className={t3} aria-label={t4}>{t5}</div>;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== t1 || $[24] !== t6) {
    t7 = <div className="flex flex-col gap-3">{t1}{t6}</div>;
    $[23] = t1;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  return t7;
};
export const SizeSelectorMobile = t0 => {
  const $ = _c(22);
  const {
    productType,
    selectedSizeId,
    selectedAppearanceStockState,
    onSizeSelection,
    platform
  } = t0;
  const t = useTranslations();
  let t1;
  if ($[0] !== platform || $[1] !== productType) {
    t1 = <SizeFitHintAndGuide className="px-4 sm:px-6 md:px-8 lg:px-10" productType={productType} platform={platform} />;
    $[0] = platform;
    $[1] = productType;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t) {
    t2 = t("cyoAssortmentDetailPage.selectSize");
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== onSizeSelection || $[6] !== productType.sizes || $[7] !== selectedAppearanceStockState || $[8] !== selectedSizeId || $[9] !== t) {
    let t4;
    if ($[11] !== onSizeSelection || $[12] !== selectedAppearanceStockState || $[13] !== selectedSizeId || $[14] !== t) {
      t4 = size => {
        const inStock = selectedAppearanceStockState.some(stockState => stockState.size.id === size.id && stockState.available);
        const isSelected = selectedSizeId === size.id;
        return <button key={size.id} role="option" onClick={!inStock ? undefined : () => onSizeSelection(size.id)} aria-disabled={!inStock} aria-selected={isSelected} className={clsx("inline-flex w-full cursor-pointer items-center justify-between border-b border-b-neutral-200 px-4 py-3 aria-disabled:pointer-events-none aria-disabled:opacity-50 sm:px-6 md:px-8 lg:px-10", isSelected ? "bg-neutral-100" : "bg-white")}><span className="font-semibold">{size.name} </span>{!inStock && <span className="text-sm text-red-600">{t("size.outOfStock")}</span>}</button>;
      };
      $[11] = onSizeSelection;
      $[12] = selectedAppearanceStockState;
      $[13] = selectedSizeId;
      $[14] = t;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    t3 = productType.sizes.map(t4);
    $[5] = onSizeSelection;
    $[6] = productType.sizes;
    $[7] = selectedAppearanceStockState;
    $[8] = selectedSizeId;
    $[9] = t;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[16] !== t2 || $[17] !== t3) {
    t4 = <div role="listbox" aria-label={t2} aria-orientation="vertical" className="flex flex-col border-t first:border-t-neutral-200">{t3}</div>;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== t1 || $[20] !== t4) {
    t5 = <>{t1}{t4}</>;
    $[19] = t1;
    $[20] = t4;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  return t5;
};